import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import PublishIcon from "@mui/icons-material/Publish";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, Grid, IconButton, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReturnNodeDto } from "~/api/GeneratedApi";

interface Props {
  selectedNode: ReturnNodeDto | null;
  refetch: () => void;
  createNewFolder: (node: ReturnNodeDto) => void;
  uploadFiles?: (parentNodeId: number, files: File[]) => void;
  onClose?: () => void;
  embedded?: boolean;
}

const useStyles = makeStyles(() => ({
  toolbarIcon: {
    color: "#8f8f8f",
  },
  toolbar: {
    backgroundColor: "#d4d9dc",
    borderBottom: "1px solid #666",
  },
  fileUploadInput: {
    display: "none",
  },
}));

const FileExplorerToolBar = ({
  selectedNode,
  refetch,
  createNewFolder,
  uploadFiles,
  onClose,
  embedded,
}: Props): JSX.Element => {
  const { t } = useTranslation("file-explorer");
  const classes = useStyles();
  const fileUploadInputRef = useRef<HTMLInputElement | null>(null);

  const disableMutations =
    selectedNode?.programManaged ||
    selectedNode?.reserved ||
    selectedNode?.parentReserved;

  return (
    <Grid
      container
      item
      style={{ padding: 5 }}
      className={classes.toolbar}
      sx={{
        borderTopLeftRadius: (theme) => ({
          sx: 0,
          md: embedded ? 0 : theme.shape.borderRadius,
        }),
        borderTopRightRadius: (theme) => ({
          sx: 0,
          md: embedded ? 0 : theme.shape.borderRadius,
        }),
      }}
    >
      <input
        multiple
        ref={fileUploadInputRef}
        className={classes.fileUploadInput}
        type="file"
        onChange={(x) => {
          if (!selectedNode) return;
          if (x.target.files?.length === 0 ?? true) return;
          const files: File[] = [].map.call(
            x.target.files,
            (file) => file,
          ) as File[];
          uploadFiles?.(selectedNode.id, files);
        }}
      />
      <Grid item style={{ marginRight: 3 }}>
        <Tooltip
          title={
            disableMutations ? t("folder-is-program-managed").toString() : ""
          }
        >
          <span>
            <Button
              size="small"
              startIcon={
                <CreateNewFolderIcon className={classes.toolbarIcon} />
              }
              disabled={disableMutations ?? true}
              onClick={() => createNewFolder(selectedNode!)}
            >
              {t("new-folder")}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <Button
          size="small"
          disabled={selectedNode?.programManaged ?? true}
          startIcon={<PublishIcon className={classes.toolbarIcon} />}
          onClick={() => fileUploadInputRef.current?.click()}
        >
          {t("upload-file")}
        </Button>
      </Grid>
      <Grid item>
        <Button
          size="small"
          startIcon={<RefreshIcon className={classes.toolbarIcon} />}
          onClick={() => refetch()}
        >
          {t("general:refresh")}
        </Button>
      </Grid>
      {onClose && (
        <Grid item sx={{ ml: "auto" }}>
          <IconButton size="small" onClick={() => onClose()}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};

export default FileExplorerToolBar;
